import { useEffect, useRef, useState, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useTranslation } from "react-i18next";
import { Container } from "@/components/shared";
import PrivacyNote from "@/components/PrivacyNote";
import { X, MapPin, CalendarDays, Clock, Ticket, Volume2, Heart, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Tout" },
  { key: "today", label: "Aujourd'hui" },
  { key: "weekend", label: "Ce week-end" },
  { key: "wednesday", label: "Mercredi" },
  { key: "vacances", label: "Vacances" },
  { key: "free", label: "Gratuit" },
];

// Territoires affichables sur la carte Kids. Architecture extensible : ajouter une entrée
// (avec un center/zoom) suffit pour prendre en charge un nouveau territoire.
const TERRITORIES = [
  { key: "metropole", label: "🇫🇷 France métropolitaine", center: [46.6, 2.4], zoom: 5 },
  { key: "guadeloupe", label: "🌴 Guadeloupe", center: [16.24, -61.55], zoom: 10 },
  { key: "martinique", label: "🌺 Martinique", center: [14.64, -61.0], zoom: 10 },
];

const pinIcon = L.divIcon({
  className: "", iconSize: [34, 34], iconAnchor: [17, 34],
  html: '<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:linear-gradient(135deg,#8B5CF6,#B19CD9);box-shadow:0 3px 8px rgba(0,0,0,.3);border:2px solid #fff"></div>',
});

function inScope(e, scope) {
  if (scope === "all") return true;
  if (scope === "free") return e.is_free;
  const tags = e.tags || [];
  if (scope === "vacances") return tags.includes("vacances");
  const d = new Date(e.date + "T00:00:00");
  const now = new Date();
  if (scope === "today") return e.date === now.toISOString().slice(0, 10);
  if (scope === "wednesday") return d.getDay() === 3 || tags.includes("mercredi");
  if (scope === "weekend") return d.getDay() === 6 || d.getDay() === 0 || tags.includes("weekend");
  return true;
}

// Carte vivante ET visible (non masquée par la vue Liste, non détruite) ?
function mapReady(map) {
  if (!map || !map._loaded || !map._mapPane) return false;
  const c = map.getContainer && map.getContainer();
  return !!c && c.isConnected && c.offsetWidth > 0 && c.offsetHeight > 0;
}
function invalidateMap(map) {
  if (!mapReady(map)) return;
  try { map.invalidateSize({ animate: false, pan: false }); } catch (_) {}
}

export default function KidsMap() {
  const { user } = useAuth();
  const { lang } = useLang();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const minor = user?.is_minor || user?.profile === "child" || user?.profile === "teen";
  const mapEl = useRef(null); const mapRef = useRef(null); const layerRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [cats, setCats] = useState([]);
  const [scope, setScope] = useState("all");
  const [cat, setCat] = useState("");
  const [region, setRegion] = useState("");
  const [territory, setTerritory] = useState("metropole");
  const [sel, setSel] = useState(null);
  const [view, setView] = useState("map");
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    api.get("/kids-events").then(({ data }) => setEvents(data.events || [])).catch(() => {});
    api.get("/kids-events/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
  }, []);

  const regions = useMemo(() => [...new Set(events.filter((e) => (e.territory || "metropole") === territory).map((e) => e.region).filter(Boolean))].sort(), [events, territory]);
  const filtered = useMemo(() => events.filter((e) =>
    (e.territory || "metropole") === territory &&
    inScope(e, scope) && (!cat || e.category === cat) && (!region || e.region === region)), [events, scope, cat, region, territory]);
  const catMeta = (k) => cats.find((c) => c.key === k) || {};
  const catLabel = (k) => catMeta(k).label || k;
  const catEmoji = (k) => catMeta(k).emoji || "🎈";

  const speak = (e) => {
    if (!("speechSynthesis" in window)) { toast.error(t("kids.audioUnavailable")); return; }
    // Toggle stop si une lecture est en cours
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    window.speechSynthesis.cancel();
    const en = lang === "en";
    const text = en ? (e.child_audio_en || e.child_audio || e.description || e.name)
                    : (e.child_audio || e.description || e.name);
    const u = new SpeechSynthesisUtterance(text);
    u.lang = en ? "en-US" : "fr-FR"; u.rate = 0.95; u.pitch = 1.05;
    const voices = window.speechSynthesis.getVoices();
    const rx = en ? /en(-|_|$)/i : /fr(-|_|$)/i;
    const fem = voices.find((v) => rx.test(v.lang) && /(Am[ée]lie|Audrey|Marie|Julie|Virginie|C[ée]line|Samantha|Karen|Zira|Female|f[ée]minin)/i.test(v.name)) || voices.find((v) => rx.test(v.lang));
    if (fem) u.voice = fem;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const closeDetail = () => {
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setSpeaking(false); setSel(null);
  };

  useEffect(() => () => { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); }, []);

  const sendWish = async (e) => {
    try {
      const { data } = await api.post("/kids-wishes", { event_id: e.event_id });
      if (data && data.linked) {
        toast.success(t("kids.wishSent"));
      } else {
        toast(t("kids.wishNoParent"), { duration: 6000 });
      }
    } catch (_) { toast.error(t("kids.wishError")); }
  };

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([46.6, 2.4], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png", { maxZoom: 19, subdomains: "abc",
      attribution: "&copy; OpenStreetMap France" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const t0 = setTimeout(() => invalidateMap(map), 200);
    return () => { clearTimeout(t0); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const lg = layerRef.current; if (!lg) return;
    lg.clearLayers();
    filtered.forEach((e) => {
      if (e.lat == null || e.lng == null) return;
      L.marker([e.lat, e.lng], { icon: pinIcon }).on("click", () => setSel(e)).addTo(lg);
    });
  }, [filtered]);

  useEffect(() => {
    const m = mapRef.current;
    const t = TERRITORIES.find((x) => x.key === territory);
    if (!m || !t || !mapReady(m)) return;
    m.setView(t.center, t.zoom, { animate: false });
    const id = setTimeout(() => invalidateMap(m), 150);
    return () => clearTimeout(id);
  }, [territory]);

  useEffect(() => {
    if (view !== "map") return;
    const m = mapRef.current;
    const applyTerr = () => {
      const t = TERRITORIES.find((x) => x.key === territory);
      if (m && t && mapReady(m)) m.setView(t.center, t.zoom, { animate: false });
    };
    const ids = [
      setTimeout(() => { invalidateMap(m); applyTerr(); }, 120),
      setTimeout(() => invalidateMap(m), 400),
    ];
    return () => ids.forEach(clearTimeout);
  }, [view, filtered, territory]);

  return (
    <Container className="max-w-4xl">
      <div data-testid="kids-map-page">
        <h1 className="font-display text-3xl mb-1" style={{ color: "var(--accent-strong)" }}>{t("kids.title")}</h1>
        <p className="text-base mb-4" style={{ color: "var(--text-main)" }}>{t("kids.subtitle")}</p>
        <PrivacyNote className="mb-4" text={t("privacy.kids")} />

        {/* Sélecteur de territoire */}
        <div className="mb-3">
          <div className="flex flex-wrap gap-2" data-testid="kids-territory">
            {TERRITORIES.map((tr) => (
              <button key={tr.key} data-testid={`kids-territory-${tr.key}`}
                onClick={() => { setTerritory(tr.key); setRegion(""); }}
                className="text-sm font-semibold rounded-full px-4 py-2 transition-transform hover:scale-105"
                style={territory === tr.key
                  ? { background: "var(--accent-gradient)", color: "#fff" }
                  : { background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent-strong)" }}>
                {t(`kids.terr.${tr.key}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Filtres ludiques */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="kids-filters">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`kids-filter-${f.key}`} onClick={() => setScope(f.key)}
              className="text-sm font-semibold rounded-full px-4 py-2 transition-transform hover:scale-105"
              style={scope === f.key
                ? { background: "var(--accent-gradient)", color: "#fff" }
                : { background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent-strong)" }}>
              {t(`kids.filters.${f.key}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          <select data-testid="kids-cat" value={cat} onChange={(e) => setCat(e.target.value)}
            className="rounded-full px-3 py-2 text-sm bg-white/[0.06]" style={{ color: "var(--text-main)", border: "1px solid var(--surface-border)" }}>
            <option value="">{t("kids.allCats")}</option>
            {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select data-testid="kids-region" value={region} onChange={(e) => setRegion(e.target.value)}
            className="rounded-full px-3 py-2 text-sm bg-white/[0.06]" style={{ color: "var(--text-main)", border: "1px solid var(--surface-border)" }}>
            <option value="">{t("kids.allRegions")}</option>
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Bascule Carte / Liste */}
        <div className="flex rounded-full overflow-hidden mb-4 w-max" style={{ border: "2px solid var(--surface-border)" }} data-testid="kids-view-toggle">
          <button data-testid="kids-view-map" onClick={() => setView("map")}
            className="text-sm font-semibold px-5 py-2"
            style={view === "map" ? { background: "var(--accent-gradient)", color: "#fff" } : { color: "var(--accent-strong)", background: "transparent" }}>{t("kids.map")}</button>
          <button data-testid="kids-view-list" onClick={() => setView("list")}
            className="text-sm font-semibold px-5 py-2"
            style={view === "list" ? { background: "var(--accent-gradient)", color: "#fff" } : { color: "var(--accent-strong)", background: "transparent" }}>{t("kids.list")}</button>
        </div>

        {/* Carte interactive */}
        <div ref={mapEl} data-testid="kids-map-canvas" className={"w-full h-[360px] rounded-3xl overflow-hidden mb-6 shadow-md " + (view === "map" ? "" : "hidden")} style={{ border: "2px solid var(--surface-border)" }} />

        {/* Liste des activités */}
        <div className={"grid grid-cols-1 sm:grid-cols-2 gap-4 " + (view === "list" ? "" : "hidden")} data-testid="kids-events-list">
          {filtered.length === 0 && <p className="text-sm col-span-full" style={{ color: "color-mix(in srgb,var(--text-main) 60%,transparent)" }}>{t("kids.noResults")}</p>}
          {filtered.map((e) => (
            <button key={e.event_id} data-testid={`kids-event-${e.event_id}`} onClick={() => setSel(e)}
              className="rounded-3xl p-4 text-left shadow-sm transition-transform hover:scale-[1.02]" style={{ background: "var(--bg-surface)", border: "1px solid var(--surface-border)" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: "color-mix(in srgb,var(--accent) 15%,transparent)", color: "var(--accent-strong)" }}>{catEmoji(e.category)} {catLabel(e.category)}</span>
                {e.is_free && <span className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: "#DCFCE7", color: "#166534" }}>{t("kids.free")}</span>}
              </div>
              <p className="font-display text-lg leading-tight" style={{ color: "var(--text-main)" }}>{e.name}</p>
              <p className="text-sm mt-1 flex items-center gap-1" style={{ color: "color-mix(in srgb,var(--text-main) 65%,transparent)" }}><CalendarDays size={13} /> {e.date}{e.time ? ` · ${e.time}` : ""}</p>
              <p className="text-sm flex items-center gap-1" style={{ color: "color-mix(in srgb,var(--text-main) 65%,transparent)" }}><MapPin size={13} /> {e.city} · {e.age_min}-{e.age_max} {t("kids.years")}</p>
            </button>
          ))}
        </div>
      </div>

      {sel && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4" style={{ background: "rgba(0,0,0,.4)" }} onClick={closeDetail}>
          <div data-testid="kids-event-detail" onClick={(ev) => ev.stopPropagation()}
            className="w-full max-w-md rounded-3xl p-5 max-h-[85vh] overflow-y-auto" style={{ background: "var(--bg-surface)" }}>
            <div className="flex justify-between items-start mb-2">
              <h2 className="font-display text-2xl" style={{ color: "var(--accent-strong)" }}>{sel.name}</h2>
              <button data-testid="kids-detail-close" onClick={closeDetail} className="text-white/50"><X size={22} /></button>
            </div>
            {sel.image ? <img src={sel.image} alt="" className="w-full h-40 object-cover rounded-2xl mb-3" /> : null}
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: "color-mix(in srgb,var(--accent) 15%,transparent)", color: "var(--accent-strong)" }}>{catLabel(sel.category)}</span>
              <span className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: "#EEF2FF", color: "#4338CA" }}>{sel.age_min}-{sel.age_max} {t("kids.years")}</span>
              <span className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: sel.is_free ? "#DCFCE7" : "#FEF3C7", color: sel.is_free ? "#166534" : "#92400E" }}>{sel.is_free ? t("kids.free") : (sel.price || t("kids.paid"))}</span>
            </div>
            <p className="text-sm mb-2" style={{ color: "var(--text-main)" }}>{sel.description}</p>
            <p className="text-sm flex items-center gap-2" style={{ color: "color-mix(in srgb,var(--text-main) 70%,transparent)" }}><CalendarDays size={14} /> {sel.date}{sel.end_date ? ` → ${sel.end_date}` : ""} {sel.time ? `· ${sel.time}` : ""}</p>
            <p className="text-sm flex items-center gap-2" style={{ color: "color-mix(in srgb,var(--text-main) 70%,transparent)" }}><MapPin size={14} /> {sel.address}, {sel.city} ({sel.region})</p>
            {minor && (
              <div className="mt-3 rounded-2xl p-3 flex items-start gap-2 text-sm" style={{ background: "color-mix(in srgb,var(--accent) 8%,transparent)", color: "var(--accent-strong)" }}>
                <ShieldQuestion size={16} className="mt-0.5 shrink-0" /> {t("kids.safety")}
              </div>
            )}
            <button data-testid="kids-listen-btn" onClick={() => speak(sel)}
              className="mt-4 w-full rounded-2xl py-4 font-display text-xl flex items-center justify-center gap-2"
              style={{ background: speaking ? "#FFD24D" : "#FFE58A", color: "#7A5A00" }}>
              <Volume2 size={24} /> {speaking ? t("kids.stop") : t("kids.listen")}
            </button>
            <button data-testid="kids-want-btn" onClick={() => sendWish(sel)}
              className="mt-3 w-full rounded-2xl py-4 font-display text-xl text-white flex items-center justify-center gap-2" style={{ background: "var(--accent-gradient)" }}>
              <Heart size={22} /> {t("kids.wantToGo")}
            </button>
          </div>
        </div>
      )}
    </Container>
  );
}
